import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import { useSession } from "../lib/auth-client";
import { consumeReturnTo, readReturnTo, returnToPath } from "../lib/auth-return";
import { clearRbacCache } from "../hooks/use-rbac";

/**
 * The OAuth GET callback redirects here after better-auth has issued the
 * session cookie. A full-page replace then loads the original deep link with
 * that cookie visible to useSession().
 */
export function AuthReturn() {
  const { data: session, isPending } = useSession();
  const location = useLocation();
  const returnTo = readReturnTo(location.search);
  const redirectStarted = useRef(false);

  useEffect(() => {
    // StrictMode runs mount effects twice in development. Guard the consume +
    // replace pair so the second pass cannot consume "/" and overwrite the
    // real destination before navigation completes.
    if (!isPending && session?.user && !redirectStarted.current) {
      redirectStarted.current = true;
      // #4952 round 3: a sign-in can be a different person from the last one
      // on this browser; the deep link loads with no RBAC warm-load cache.
      clearRbacCache();
      window.location.replace(consumeReturnTo(location.search));
    }
  }, [isPending, location.search, session?.user]);

  if (!isPending && !session?.user) {
    return <Navigate to={returnToPath("/login", returnTo)} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-muted-foreground text-sm">Completing sign-in...</div>
    </div>
  );
}
